import type { ShipmentPiece, ShipmentPieceProduct } from '@/apis/types';
import { CustomTitle } from '@/components';
import { Flex } from 'antd';
import React from 'react';
import { weightKgToLb } from '../shellShipmentHelpers';
import styles from './ShipmentPackagesSection.module.css';
import StatusTag from './StatusTag';
import {
  countDeliveredPieces,
  getPieceStatusMeta,
  getPieceTrackingUrl,
} from './shipmentPieces';

interface ShipmentPackagesSectionProps {
  pieces?: ShipmentPiece[];
  courierTitle?: string | null;
}

const formatWeight = (weightKg: number | null): string => {
  const lb = weightKgToLb(weightKg);
  return lb === undefined ? '--' : `${weightKg} kg / ${lb} lb`;
};

const formatContents = (product: ShipmentPieceProduct): string =>
  [
    `${product.name} × ${product.quantity}`,
    product.serial_number && `Serial ${product.serial_number}`,
    product.lot_number && `Lot ${product.lot_number}`,
    product.expiration_date && `Exp ${product.expiration_date}`,
  ]
    .filter(Boolean)
    .join(' · ');

const ShipmentPackagesSection: React.FC<ShipmentPackagesSectionProps> = ({
  pieces,
  courierTitle,
}) => {
  if (!pieces?.length) return null;

  const total = pieces.length;
  const sorted = [...pieces].sort((a, b) => a.sequence - b.sequence);

  return (
    <Flex vertical gap={8} data-testid='shipment-packages-section'>
      <Flex justify='space-between' align='center' gap={16}>
        <CustomTitle
          text='Packages'
          fontSize={15}
          fontWeight={700}
          lineHeight='28px'
          color='var(--text-primary)'
          marginBottom='0'
        />
        <span className={styles.delivered}>
          {countDeliveredPieces(pieces)} of {total} delivered
        </span>
      </Flex>
      {sorted.map((piece) => {
        const status = getPieceStatusMeta(piece.status);
        const trackingUrl = getPieceTrackingUrl(
          courierTitle,
          piece.tracking_number
        );
        const boxLabel = `Box ${piece.sequence} of ${total}`;
        return (
          <div key={piece.id} className={styles.card}>
            <div className={styles.cardHeader}>
              <span className={styles.label}>{boxLabel}</span>
              <StatusTag type={status.type} status={status.label} />
            </div>
            {trackingUrl ? (
              <a
                href={trackingUrl}
                target='_blank'
                rel='noopener noreferrer'
                className={styles.trackingLink}
                aria-label={`Track ${boxLabel}, ${piece.tracking_number}`}
              >
                {piece.tracking_number}
              </a>
            ) : (
              <span className={styles.trackingLink}>
                {piece.tracking_number}
              </span>
            )}
            <span className={styles.secondary}>
              {formatWeight(piece.weight_kg)}
            </span>
            {piece.products.length > 0 && (
              <ul className={styles.contents}>
                {piece.products.map((product) => (
                  <li key={product.product_id} className={styles.secondary}>
                    {formatContents(product)}
                  </li>
                ))}
              </ul>
            )}
          </div>
        );
      })}
    </Flex>
  );
};

export default ShipmentPackagesSection;
